import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import { buildCmmdTaskGoal } from "@cmmd-forge/shared/cmmdTaskGoal";
import type {
  CmmdBoardTask,
  CmmdProject,
  CmmdTaskExecutionOption,
  EnvironmentApi,
  EnvironmentId,
  ModelSelection,
  ProjectId,
  ServerProvider,
  ThreadId,
} from "@cmmd-forge/contracts";

import {
  hasPotentialProviderAuthority,
  type ForgeProviderAccessState,
  type ProviderCredentialMode,
} from "~/cmmd/forgeProviderAccess";
import { resolveRunnableProviderSnapshots } from "~/cmmd/providerAvailabilityJoin";
import { readEnvironmentApi } from "~/environmentApi";
import { classifyEnvironmentId } from "~/environments/environmentIdClassification";
import {
  NO_AUTHORIZED_PROVIDER_MESSAGE,
  resolveProviderInventoryReadinessError,
} from "~/lib/providerInventoryReadiness";
import { newCommandId, newThreadId } from "~/lib/utils";
import { selectProjectsAcrossEnvironments, useStore } from "~/store";
import {
  prepareThreadProviderLaunch,
  startThreadForItem,
  type PreparedThreadProviderLaunch,
  type ThreadLaunchConfiguration,
} from "../NoActiveThreadState.threadRunner";
import { buildTaskThreadContextPrompt } from "../command-center/taskThreadContext";
import {
  startOrResumeTaskWorkSession,
  type ExistingTaskConversation,
  type TaskWorkSessionCheckpoint,
  type TaskWorkSessionTarget,
} from "./taskWorkSessionLaunch";
import {
  ensureTaskWorkTargetConnection,
  resolveTaskWorkTargetProjectRef,
} from "./taskWorkTargetConnection";

export const NO_AUTHORIZED_FORGE_WORKSPACE_MESSAGE =
  "No Forge project is available on an authorized runtime for this task.";

export interface TaskWorkTarget extends TaskWorkSessionTarget {
  readonly key: string;
  readonly organizationId: number;
  readonly runtime: CmmdTaskExecutionOption["runtime"];
  readonly availability: CmmdTaskExecutionOption["availability"];
  readonly unavailableReason: string | null;
  readonly workspaceRoot: string | null;
  readonly githubRepoFullName: string | null;
}

export const DEFAULT_CONFIGURATION: ThreadLaunchConfiguration = {
  modelSelection: null,
  runtimeMode: "full-access",
  interactionMode: "default",
  environmentMode: "worktree",
  baseBranch: "main",
};

export interface LauncherState {
  readonly task: CmmdBoardTask | null;
  readonly options: readonly CmmdTaskExecutionOption[];
  readonly selectedTargetKey: string | null;
  readonly loading: boolean;
  readonly starting: boolean;
  readonly error: string | null;
  readonly existingConversation: ExistingTaskConversation | null;
  readonly configuration: ThreadLaunchConfiguration;
}

export const CLOSED_STATE: LauncherState = {
  task: null,
  options: [],
  selectedTargetKey: null,
  loading: false,
  starting: false,
  error: null,
  existingConversation: null,
  configuration: DEFAULT_CONFIGURATION,
};

/**
 * Which credential pool a work session's provider check reads from: the CMMD
 * cloud pool for any cloud-shaped target environment, the runtime's own pool
 * otherwise. Pulled out of the hook so the durable `env_` case has a seam: the
 * hook's own environmentId comes from a picked target, not from props, so it
 * cannot be exercised without mounting the whole picker.
 */
export function resolveTaskWorkSessionCredentialMode(
  selectedTarget: { readonly environmentId: string } | null,
): ProviderCredentialMode {
  return selectedTarget !== null && classifyEnvironmentId(selectedTarget.environmentId).isCloud
    ? "cmmd-cloud"
    : "runtime";
}

/**
 * The providers a task launch may build on: those whose Run verdict is usable.
 *
 * A launch is an execution, so this is the join's Run projection and not a
 * display filter (forge#3396). The eligibility filter this used to consult
 * ignored the published wire, so a provider the Environment withheld still
 * opened the launch gate when its legacy flags said ready. Refusals render
 * through `resolveTaskWorkProviderLaunchReadiness`'s existing copy path.
 */
export function resolveTaskWorkRunnableProviders(input: {
  readonly providers: readonly ServerProvider[];
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): readonly ServerProvider[] {
  // A task launch reads a live, published inventory, never CMMD's last-known
  // catalog, so this preserves the Run verdict's prior always-live behavior.
  return resolveRunnableProviderSnapshots({ ...input, hasLiveInventory: true });
}

export function resolveTaskWorkProviderLaunchReadiness(input: {
  readonly providers: readonly ServerProvider[];
  readonly runnableProviders: readonly ServerProvider[];
  readonly inventoryKnown: boolean;
  readonly access: ForgeProviderAccessState;
}): { readonly canStart: boolean; readonly unavailableReason: string | null } {
  if (input.access.status === "loading") {
    return { canStart: false, unavailableReason: null };
  }
  if (input.access.status === "error") {
    return {
      canStart: false,
      unavailableReason:
        "Forge could not verify provider access. Try again, or check provider connections in Settings.",
    };
  }
  if (input.runnableProviders.length > 0) {
    return { canStart: true, unavailableReason: null };
  }
  const potentialAuthority = hasPotentialProviderAuthority(input.access);
  if (!input.inventoryKnown && potentialAuthority) {
    return { canStart: true, unavailableReason: null };
  }
  return {
    canStart: false,
    unavailableReason: potentialAuthority
      ? resolveProviderInventoryReadinessError(input.providers)
      : NO_AUTHORIZED_PROVIDER_MESSAGE,
  };
}

export interface PreparedTaskThreadLaunch {
  readonly projectRef: { readonly environmentId: EnvironmentId; readonly projectId: ProjectId };
  readonly projects: ReturnType<typeof selectProjectsAcrossEnvironments>;
  readonly providerLaunch: PreparedThreadProviderLaunch;
}

export async function prepareTaskThreadLaunch(input: {
  readonly launchTarget: TaskWorkTarget;
  readonly project: CmmdProject;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryProviders: readonly ServerProvider[];
  readonly preferredModelSelection: ModelSelection | null;
}): Promise<PreparedTaskThreadLaunch> {
  await ensureTaskWorkTargetConnection({
    target: input.launchTarget,
    project: input.project,
    primaryEnvironmentId: input.primaryEnvironmentId,
  });
  const currentStore = useStore.getState();
  const projectRef = resolveTaskWorkTargetProjectRef({
    target: input.launchTarget,
    cmmdProjectId: String(input.project.id),
    environmentStateById: currentStore.environmentStateById,
  });
  const providerLaunch = await prepareThreadProviderLaunch({
    environmentId: projectRef.environmentId,
    primaryEnvironmentId: input.primaryEnvironmentId,
    primaryProviders: input.primaryProviders,
    preferred: input.preferredModelSelection,
    pinPreferredProvider: input.preferredModelSelection !== null,
  });
  return {
    projectRef,
    projects: selectProjectsAcrossEnvironments(currentStore),
    providerLaunch,
  };
}

export function resolveStartContext(
  state: LauncherState,
  selectedTarget: TaskWorkTarget | null,
  project: CmmdProject | null,
  canStartNewConversation: boolean,
): {
  readonly task: CmmdBoardTask;
  readonly project: CmmdProject;
  readonly launchTarget: TaskWorkTarget | null;
} | null {
  if (state.task === null || project === null) return null;
  const launchTarget =
    selectedTarget !== null && isReadyTaskWorkTarget(selectedTarget) ? selectedTarget : null;
  if (state.existingConversation === null && (launchTarget === null || !canStartNewConversation)) {
    return null;
  }
  return { task: state.task, project, launchTarget };
}

export function requireReadyLaunchTarget(target: TaskWorkTarget | null): TaskWorkTarget {
  if (target !== null) return target;
  throw new Error("A ready Forge runtime is required to create a task conversation.");
}

export function notifySessionStarted(
  result: Awaited<ReturnType<typeof startOrResumeTaskWorkSession>>,
  onSessionStarted: () => void,
): void {
  if (result.kind === "created") onSessionStarted();
}

export function flattenTaskWorkTargets(
  options: readonly CmmdTaskExecutionOption[],
): readonly TaskWorkTarget[] {
  return options.flatMap((option) =>
    option.forgeProjects.map((project) => ({
      key: `${option.runtime.id}:${project.linkId}`,
      organizationId: option.organizationId,
      runtimeId: option.runtime.id,
      forgeProjectLinkId: project.linkId,
      environmentId: project.environmentId,
      projectId: project.projectId,
      runtime: option.runtime,
      availability: option.availability,
      unavailableReason: option.unavailableReason,
      workspaceRoot: project.workspaceRoot,
      githubRepoFullName: project.githubRepoFullName,
    })),
  );
}

export function isReadyTaskWorkTarget(target: TaskWorkTarget): boolean {
  return target.availability === "ready";
}

export function createCheckpoint(): TaskWorkSessionCheckpoint {
  return {
    idempotencyKey: newCommandId(),
    threadId: newThreadId(),
    sessionId: null,
    threadCreated: false,
  };
}

export async function createTaskThread(input: {
  readonly task: CmmdBoardTask;
  readonly project: CmmdProject;
  readonly workspaceName: string | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly threadId: ThreadId;
  readonly projects: ReturnType<typeof selectProjectsAcrossEnvironments>;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly defaultThreadRuntime: Parameters<typeof startThreadForItem>[0]["defaultThreadRuntime"];
  readonly serverProviders: Parameters<typeof startThreadForItem>[0]["serverProviders"];
  readonly launchConfiguration: ThreadLaunchConfiguration;
  readonly preparedProviderLaunch: PreparedThreadProviderLaunch;
}) {
  const dependencyTitles = input.task.dependsOn
    .map((taskId) => findTask(input.allTasks, taskId)?.title ?? null)
    .filter((title): title is string => title !== null);
  const seedPrompt = buildTaskThreadContextPrompt({
    workspaceName: input.workspaceName,
    identityDisplayName: null,
    campaignName: input.project.campaignTitle ?? null,
    campaignDescription: null,
    projectName: input.project.title,
    projectDescription: input.project.description,
    task: {
      id: String(input.task.id),
      title: input.task.title,
      status: input.task.status,
      priority: input.task.priority,
      dueDate: input.task.dueDate,
      description: input.task.description,
      dependsOn: input.task.dependsOn.map(String),
    },
    dependencyTitles,
  });
  const targetRef = scopeProjectRef(input.environmentId, input.projectId);

  await startThreadForItem({
    itemId: `cmmd:${input.task.id}`,
    threadId: input.threadId,
    autoStart: true,
    launchConfiguration: input.launchConfiguration,
    importedProjectRef: targetRef,
    importedFromCmmdProject: true,
    payload: {
      title: input.task.title,
      seedPrompt,
      cmmdTaskId: String(input.task.id),
      goal: buildCmmdTaskGoal({
        projectId: input.project.id,
        taskId: input.task.id,
        title: input.task.title,
        description: input.task.description,
        updatedAt: input.task.updatedAt,
      }),
    },
    workspaceProject: null,
    defaultProjectRef: targetRef,
    projects: input.projects,
    primaryEnvironmentId: input.primaryEnvironmentId,
    defaultThreadRuntime: input.defaultThreadRuntime,
    serverProviders: input.serverProviders,
    preparedProviderLaunch: input.preparedProviderLaunch,
    cmmdTaskTitleById: new Map([[String(input.task.id), input.task.title]]),
    resolveImportedCmmdProjectRef: () => targetRef,
    readEnvironmentApi: readEnvironmentApi as (
      environmentId: EnvironmentId,
    ) => EnvironmentApi | undefined,
  });
}

function findTask(tasks: readonly CmmdBoardTask[], taskId: number): CmmdBoardTask | null {
  for (const task of tasks) {
    if (task.id === taskId) return task;
    const child = findTask(task.children, taskId);
    if (child) return child;
  }
  return null;
}
